import { CommonModule } from '@angular/common';
import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { IonButton, IonIcon, IonItem, IonLabel, IonList, LoadingController } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessagesUiService, MessageUiIonicComponent } from '@okcargo-apps/messages-ui';
import { EDocumentType } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { DateFnsModule } from 'ngx-date-fns';
import { AccountService } from '../../account/account.service';
import { DownloadDocumentService } from '../../download-document/download-document.service';
import { FastpayCardCtaComponent } from '../../fast-payment';
import { CompanyService } from '../company.service';
import { UploadDocumentButtonComponent } from '../upload-document-button/upload-document-button.component';

@Component({
  selector: 'lib-company-docs',
  templateUrl: './company-docs.component.html',
  styleUrls: ['./company-docs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonIcon,
    CommonModule,
    IonButton,
    IonLabel,
    IonItem,
    IonList,
    TranslateModule,
    DateFnsModule,
    UploadDocumentButtonComponent,
    MessageUiIonicComponent,
    FastpayCardCtaComponent,
  ],
})
export class CompanyDocsComponent {
  private readonly accountService = inject(AccountService);
  private readonly companyService = inject(CompanyService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly connectivity = inject(ConnectivityService);

  /** Downloading a document queries the backend: it cannot be done without connection. */
  protected readonly isOffline = this.connectivity.isOffline;
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly messagesUiService = inject(MessagesUiService);

  public readonly EDocumentType = EDocumentType;
  public readonly todayTimestamp: number = new Date().getTime();

  public readonly account = this.accountService.account;

  protected readonly canAdministrativeDocuments = computed(() =>
    this.accountService.hasCapability(ECapabilities.ADMINISTRATIVE_DOCUMENTS),
  );
  protected readonly canViewPrices = computed(() => this.accountService.hasCapability(ECapabilities.VIEW_PRICES));

  public readonly companyDocuments = this.companyService.companyDocuments;
  public readonly messages = computed(() => this.messagesUiService.getMessagesForObjectId('companyDocs'));

  // constructor() {}

  public async downloadDocument(url: string): Promise<void> {
    if (this.isOffline()) return;

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.downloading_file'),
    });
    await loading.present();
    try {
      await this.downloadDocumentService.downloadFile(url);
    } catch (error) {
      // TODO implement a message for the user
      // or remove try/catch and use ErrorService if the backend fix error codes
      // (now comes code 500, must be error 404 file not found)
      console.error('Error al descargar el documento', error);
    } finally {
      await loading.dismiss();
    }
  }
}
